import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, FileSignature, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useTenantStore } from '@/hooks/useTenantStore';
import { CreateResolutionModal } from '@/components/board/CreateResolutionModal';
import { ResolutionDetail } from '@/components/board/ResolutionDetail';
import { BoardRosterTool } from '@/components/board/BoardRosterTool';
import { BoardAnalytics } from '@/components/board/BoardAnalytics';
import { GovernanceReports } from '@/components/board/GovernanceReports';
import { RoleGate } from '@/components/auth/RoleGate';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

interface Resolution {
  id: string;
  title: string;
  status: string;
  created_at: string;
}

export default function BoardPortal() {
  const { tenantId } = useTenantStore();
  const [resolutions, setResolutions] = useState<Resolution[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  async function loadResolutions() {
    if (!tenantId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('board_resolutions')
        .select('id, title, status, created_at')
        .eq('company_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setResolutions(data || []);
    } catch (err) {
      console.error('Error loading resolutions:', err);
      toast.error('Failed to load resolutions');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!tenantId) return;

    loadResolutions();

    const channel = supabase
      .channel('board_portal_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'board_resolutions',
          filter: `company_id=eq.${tenantId}`,
        },
        loadResolutions
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'board_signatures' },
        loadResolutions
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenantId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-500';
      case 'REJECTED':
        return 'bg-red-500';
      case 'IN_CIRCULATION':
        return 'bg-blue-500';
      case 'DRAFT':
        return 'bg-gray-500';
      case 'ARCHIVED':
        return 'bg-gray-400';
      default:
        return 'bg-gray-500';
    }
  };

  if (!tenantId) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <p className="text-muted-foreground">Please select a company first.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Board Analytics */}
        <BoardAnalytics companyId={tenantId} />

        {/* Governance Reports */}
        <RoleGate allowedRoles={['owner', 'admin']} companyId={tenantId}>
          <GovernanceReports companyId={tenantId} />
        </RoleGate>

        {/* Board Roster Management */}
        <RoleGate allowedRoles={['owner', 'admin']} companyId={tenantId}>
          <BoardRosterTool companyId={tenantId} />
        </RoleGate>

        {/* Resolutions Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-xl font-semibold">Board Resolutions</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadResolutions}>
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
              <RoleGate allowedRoles={['owner', 'admin']} companyId={tenantId}>
                <CreateResolutionModal companyId={tenantId} onCreated={loadResolutions} />
              </RoleGate>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : resolutions.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resolutions.map((resolution) => (
                      <TableRow key={resolution.id}>
                        <TableCell className="font-medium">{resolution.title}</TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(resolution.status)}>
                            {resolution.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(resolution.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedId(resolution.id)}
                            className="flex items-center gap-2"
                          >
                            <FileSignature className="h-4 w-4" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-center text-sm text-muted-foreground py-8">
                No resolutions yet. Create your first resolution to get started.
              </p>
            )}
          </CardContent>
        </Card>

        {selectedId && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Resolution Details</CardTitle>
            </CardHeader>
            <CardContent>
              <ResolutionDetail resolutionId={selectedId} refresh={loadResolutions} />
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
